import { isUuid } from './uuid';

export const MAX_PRICE_TIER_ITEMS = 10;
export const PRICE_TIER_NAME_MAX = 40;
export const PRICE_TIER_DESCRIPTION_MAX = 80;

const GROUP_LISTING_TYPES = new Set(['instant-booking', 'free-booking', 'paylater-booking']);

const WINDOW_MODES = new Set(['absolute', 'relativeSalesStart', 'relativeEvent']);

let fallbackTierSeq = 0;

export const createPriceTierId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  fallbackTierSeq += 1;
  const tail = `${Date.now().toString(16)}${fallbackTierSeq.toString(16)}`
    .padStart(12, '0')
    .slice(-12);
  return `00000000-0000-4000-8000-${tail}`;
};

export const supportsRelativeEvent = listingType => GROUP_LISTING_TYPES.has(listingType);

const trimString = value => (typeof value === 'string' ? value.trim() : '');

const toFiniteNumber = value => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const toIntOrNull = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
};

const dayMs = 24 * 60 * 60 * 1000;

const parseDate = value => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object' && value.date) {
    return parseDate(value.date);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeWindow = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const mode = WINDOW_MODES.has(raw.mode) ? raw.mode : null;
  if (!mode) {
    return null;
  }
  if (mode === 'absolute') {
    return {
      mode,
      startAt: raw.startAt ? String(raw.startAt) : null,
      endAt: raw.endAt ? String(raw.endAt) : null,
    };
  }
  return {
    mode,
    startOffsetDays: toIntOrNull(raw.startOffsetDays),
    endOffsetDays: toIntOrNull(raw.endOffsetDays),
  };
};

const normalizeItem = (item, index) => {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const id = isUuid(item.id) ? item.id : null;
  const amount = toFiniteNumber(item.amount);
  const currency = trimString(item.currency).toUpperCase();
  const name = trimString(item.name);
  if (!id || amount === null || amount < 0 || !currency || !name) {
    return null;
  }
  return {
    id,
    name: name.slice(0, PRICE_TIER_NAME_MAX),
    description: trimString(item.description).slice(0, PRICE_TIER_DESCRIPTION_MAX) || null,
    amount: Math.round(amount),
    currency,
    sortOrder: toIntOrNull(item.sortOrder) ?? index,
    maxUnits: toIntOrNull(item.maxUnits),
    showUnitsLeft: !!item.showUnitsLeft && toIntOrNull(item.maxUnits) != null,
    window: normalizeWindow(item.window),
  };
};

/**
 * @returns {object|null} normalized catalog when enabled with at least one item
 */
export const normalizePriceTiers = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  if (raw.enabled !== true) {
    return null;
  }
  if (!Array.isArray(raw.items)) {
    return null;
  }
  const items = raw.items.map(normalizeItem).filter(Boolean);
  if (items.length === 0) {
    return null;
  }
  return {
    enabled: true,
    showCompareAt: false,
    showUnavailable: !!raw.showUnavailable,
    salesStartAt: raw.salesStartAt ? String(raw.salesStartAt) : null,
    items: items.sort((a, b) => a.sortOrder - b.sortOrder),
  };
};

const resolveRelativeBounds = (window, anchor) => {
  const start =
    window.startOffsetDays == null
      ? null
      : new Date(anchor.getTime() + window.startOffsetDays * dayMs);
  const end =
    window.endOffsetDays == null ? null : new Date(anchor.getTime() + window.endOffsetDays * dayMs);
  return { start, end };
};

/**
 * @returns {'open'|'ended'|'not_started'|'unavailable'}
 */
export const evaluateTierWindow = (tier, ctx = {}) => {
  const now = parseDate(ctx.now) || new Date();
  const window = tier?.window;
  if (!window) {
    return 'open';
  }

  let start = null;
  let end = null;

  if (window.mode === 'absolute') {
    start = parseDate(window.startAt);
    end = parseDate(window.endAt);
  } else if (window.mode === 'relativeSalesStart') {
    // Individual sessions and content: offsets count from when the listing was published.
    const anchor = parseDate(ctx.publishedAt) || parseDate(ctx.listingCreatedAt);
    if (!anchor) {
      return 'unavailable';
    }
    ({ start, end } = resolveRelativeBounds(window, anchor));
  } else if (window.mode === 'relativeEvent') {
    if (!supportsRelativeEvent(ctx.listingType)) {
      return 'unavailable';
    }
    const bookingStart = parseDate(ctx.bookingStart);
    if (!bookingStart) {
      return 'unavailable';
    }
    ({ start, end } = resolveRelativeBounds(window, bookingStart));
  } else {
    return 'unavailable';
  }

  if (start && now < start) {
    return 'not_started';
  }
  if (end && now >= end) {
    return 'ended';
  }
  return 'open';
};

export const isTierEligible = (tier, ctx = {}) => {
  if (!tier) {
    return false;
  }
  const windowState = evaluateTierWindow(tier, ctx);
  if (windowState !== 'open') {
    return false;
  }
  if (tier.maxUnits != null) {
    const sold = Number(ctx.unitsSoldByTierId?.[tier.id] || 0);
    if (sold >= tier.maxUnits) {
      return false;
    }
  }
  return true;
};

export const autoPickTier = (items, ctx = {}) => {
  const list = Array.isArray(items) ? items : [];
  const eligible = list.filter(tier => isTierEligible(tier, ctx));
  if (eligible.length === 0) {
    return null;
  }
  eligible.sort((a, b) => {
    if (a.amount !== b.amount) {
      return a.amount - b.amount;
    }
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });
  return eligible[0];
};

export const validatePriceTiersForSave = (priceTiers, { listingType, currency } = {}) => {
  if (!priceTiers || priceTiers.enabled !== true) {
    return { valid: true, error: null };
  }
  const items = Array.isArray(priceTiers.items) ? priceTiers.items : [];
  if (items.length === 0) {
    return { valid: false, error: 'incomplete' };
  }
  if (items.length > MAX_PRICE_TIER_ITEMS) {
    return { valid: false, error: 'max' };
  }
  const expectedCurrency = trimString(currency).toUpperCase();
  for (const item of items) {
    const normalized = normalizeItem(item, item.sortOrder || 0);
    if (!normalized) {
      return { valid: false, error: 'incomplete' };
    }
    if (expectedCurrency && normalized.currency !== expectedCurrency) {
      return { valid: false, error: 'currency_mismatch' };
    }
    if (normalized.window?.mode === 'relativeEvent' && !supportsRelativeEvent(listingType)) {
      return { valid: false, error: 'relative_event_unsupported' };
    }
  }
  return { valid: true, error: null };
};

export const getPriceTiersFromListing = listing =>
  normalizePriceTiers(listing?.attributes?.publicData?.priceTiers);

export const resolveTierUnitPrice = (listing, tierId, ctx = {}) => {
  const catalog = getPriceTiersFromListing(listing);
  if (!catalog) {
    return { error: 'tiers_disabled' };
  }
  const listingType = listing?.attributes?.publicData?.listingType;
  const tier = catalog.items.find(item => item.id === tierId);
  if (!tier) {
    return { error: 'unknown_tier' };
  }
  const eligibilityCtx = {
    ...ctx,
    listingType,
    publishedAt: ctx.publishedAt || listing?.attributes?.createdAt || null,
  };
  if (!isTierEligible(tier, eligibilityCtx)) {
    const windowState = evaluateTierWindow(tier, eligibilityCtx);
    if (tier.maxUnits != null) {
      const sold = Number(eligibilityCtx.unitsSoldByTierId?.[tier.id] || 0);
      if (sold >= tier.maxUnits) {
        return { error: 'sold_out' };
      }
    }
    if (windowState === 'ended' || windowState === 'not_started') {
      return { error: 'window_closed' };
    }
    return { error: 'ineligible' };
  }
  return {
    tier,
    money: { amount: tier.amount, currency: tier.currency },
  };
};

export const lowestTierAmount = catalog => {
  if (!catalog?.items?.length) {
    return null;
  }
  return catalog.items.reduce(
    (min, item) => (min == null || item.amount < min ? item.amount : min),
    null
  );
};

/**
 * Convert stored publicData.priceTiers into Final Form-friendly plain values.
 * Caller should wrap priceAmount/priceCurrency with sdkTypes.Money.
 */
export const priceTiersToFormValues = (publicData, marketplaceCurrency) => {
  const raw = publicData?.priceTiers;
  const currency =
    publicData?.currency || marketplaceCurrency || raw?.items?.[0]?.currency || 'USD';
  if (!raw || !Array.isArray(raw.items) || raw.items.length === 0) {
    return {
      priceTiersEnabled: false,
      priceTiersShowCompareAt: false,
      priceTiersShowUnavailable: false,
      priceTiersSalesStartAt: '',
      priceTierItems: [],
    };
  }
  const toLocalInput = iso => {
    if (!iso) {
      return '';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };
  return {
    priceTiersEnabled: raw.enabled === true,
    priceTiersShowCompareAt: !!raw.showCompareAt,
    priceTiersShowUnavailable: !!raw.showUnavailable,
    priceTiersSalesStartAt: toLocalInput(raw.salesStartAt),
    priceTierItems: raw.items.map((item, index) => {
      const window = item.window || null;
      return {
        id: item.id || createPriceTierId(),
        name: item.name || '',
        description: item.description || '',
        priceAmount: item.amount,
        priceCurrency: item.currency || currency,
        maxUnits: item.maxUnits != null ? String(item.maxUnits) : '',
        showUnitsLeft: !!item.showUnitsLeft && item.maxUnits != null,
        windowMode: window?.mode || 'none',
        startAt: toLocalInput(window?.startAt),
        endAt: toLocalInput(window?.endAt),
        startOffsetDays: window?.startOffsetDays != null ? String(window.startOffsetDays) : '',
        endOffsetDays: window?.endOffsetDays != null ? String(window.endOffsetDays) : '',
        sortOrder: item.sortOrder ?? index,
      };
    }),
  };
};

/**
 * Build publicData.priceTiers from Pricing form values.
 */
export const priceTiersPayloadFromFormValues = values => {
  const currency = values?.displayCurrency || values?.price?.currency;
  const items = (values?.priceTierItems || [])
    .map((row, index) => {
      const amount = row?.price?.amount;
      if (amount == null || !row?.name || !row?.id) {
        return null;
      }
      const windowMode = row.windowMode && row.windowMode !== 'none' ? row.windowMode : null;
      let window = null;
      if (windowMode === 'absolute') {
        window = {
          mode: 'absolute',
          startAt: row.startAt ? new Date(row.startAt).toISOString() : null,
          endAt: row.endAt ? new Date(row.endAt).toISOString() : null,
        };
      } else if (windowMode === 'relativeSalesStart' || windowMode === 'relativeEvent') {
        window = {
          mode: windowMode,
          startOffsetDays:
            row.startOffsetDays === '' || row.startOffsetDays == null
              ? null
              : parseInt(row.startOffsetDays, 10),
          endOffsetDays:
            row.endOffsetDays === '' || row.endOffsetDays == null
              ? null
              : parseInt(row.endOffsetDays, 10),
        };
      }
      const maxUnits =
        row.maxUnits === '' || row.maxUnits == null ? null : parseInt(row.maxUnits, 10);
      return {
        id: row.id,
        name: String(row.name).trim(),
        description: row.description ? String(row.description).trim() : null,
        amount,
        currency: row.price?.currency || currency,
        sortOrder: index,
        maxUnits: Number.isFinite(maxUnits) && maxUnits > 0 ? maxUnits : null,
        showUnitsLeft: Number.isFinite(maxUnits) && maxUnits > 0 ? !!row.showUnitsLeft : false,
        window,
      };
    })
    .filter(Boolean);

  return {
    enabled: !!values?.priceTiersEnabled && items.length > 0,
    showCompareAt: false,
    showUnavailable: !!values?.priceTiersShowUnavailable,
    items,
  };
};
